// src/curebay/CurebayDoctor/CurebayDoctorVerify.js
import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "../../components/layout/index";
import { APP_ROUTES } from "../../constants/approutes";
import { toast } from "react-toastify";
import FullScreenLoader from "../../components/FullScreenLoader";
import dayjs from "dayjs";
import { ShieldCheck, BadgeCheck, X, FileText } from "lucide-react";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : "-";
};

const getStatusBadgeStyle = (status) => {
  switch (status) {
    case "REGISTERED":
      return "bg-blue-100 text-blue-700";
    case "VERIFIED":
      return "bg-emerald-100 text-emerald-700";
    case "ACTIVE":
      return "bg-emerald-100 text-emerald-700";
    case "INACTIVE":
      return "bg-gray-200 text-gray-700";
    case "INITIATED":
      return "bg-amber-100 text-amber-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
};


const getFinanceStatusStyle = (financeApprovalStatus) => {
  if (financeApprovalStatus === 1) return "bg-emerald-100 text-emerald-700";
  if (financeApprovalStatus === 2) return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
};

const splitFullName = (fullName) => {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) return { firstName: "", middleName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], middleName: "", lastName: "" };

  return {
    firstName: parts[0],
    lastName: parts[parts.length - 1],
    middleName: parts.slice(1, -1).join(" "),
  };
};

function CurebayDoctorVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const incomingDoctorData = location.state?.doctorData || null;
  const isEdit = location.state?.isEdit || false;
  const API_URL = process.env.REACT_APP_API_URL;
console.log(incomingDoctorData,"incomingDoctorData")
  const doctorRequestId =
    incomingDoctorData?.doctorUserId ||
    incomingDoctorData?.doctorCode ||
    incomingDoctorData?.id ||
    incomingDoctorData?.newDoctorRequestId ||
    "";

  const [licenseStatus, setLicenseStatus] = useState(
    incomingDoctorData?.mciVerificationStatus === "VERIFIED" ? "VERIFIED" : ""
  );
  const [isVerifyingLicense, setIsVerifyingLicense] = useState(false);
  const [licenseVerifyResult, setLicenseVerifyResult] = useState(null);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showLicenseFailedModal, setShowLicenseFailedModal] = useState(false);
  const [licenseFailedMessage, setLicenseFailedMessage] = useState("");
  const [isManualVerifyingLicense, setIsManualVerifyingLicense] = useState(false);
  const [panVerifyStatus, setPanVerifyStatus] = useState(
    incomingDoctorData?.panVerified === true ? "VERIFIED" : ""
  );
  const [isVerifyingPan, setIsVerifyingPan] = useState(false);
  const [showPanFailedModal, setShowPanFailedModal] = useState(false);
  const [panFailedMessage, setPanFailedMessage] = useState("");
  const [isManualVerifyingPan, setIsManualVerifyingPan] = useState(false);
  const [financeApprovalStatus, setFinanceApprovalStatus] = useState(
    incomingDoctorData?.financeApprovalStatus ?? 0
  );
  const [isApproving, setIsApproving] = useState(false);
  const [isCompletingOnboard, setIsCompletingOnboard] = useState(false);
  const [doctorType, setDoctorType] = useState("FREELANCER");
  const [doctorSubType, setDoctorSubType] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");

  const [basicDetails, setBasicDetails] = useState(() => {
    const { firstName, middleName, lastName } = splitFullName(
      incomingDoctorData?.doctorName
    );
    return {
      firstName,
      middleName,
      lastName,
      mobile: incomingDoctorData?.phone || "",
      email: incomingDoctorData?.email || "",
    };
  });
  const [otp, setOtp] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [doctorUserId, setDoctorUserId] = useState("");
  const [showPanModal, setShowPanModal] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);

  const handleBasicDetailsChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "mobile") {
      newValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setBasicDetails((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleDoctorTypeChange = (e) => {
    const value = e.target.value;
    setDoctorType(value);
    if (value !== "EMPANELLED" && value !== "INVENTORY" && value !== "FREELANCER") {
      setDoctorSubType("");
    }
  };

  const handleVerifyLicense = async () => {
    const registrationNumber =
      incomingDoctorData?.medicalLicenseNumber || incomingDoctorData?.licenseNumber || "";
    const medicalCouncil = incomingDoctorData?.stateMedicalCouncil || "";

    if (!registrationNumber) {
      toast.error("Medical license number not found.");
      return;
    }

    if (!medicalCouncil) {
      toast.error("State medical council not found.");
      return;
    }

    setIsVerifyingLicense(true);
    try {
      const token = localStorage.getItem("auth_token");
      const baseUrl = API_URL?.endsWith("/") ? API_URL : `${API_URL}/`;
      const res = await fetch(`${baseUrl}api/v1/nmc-verification/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          registrationNumber,
          medicalCouncil,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok && !data?.status) {
        toast.error(data?.message || "Failed to verify medical license.");
        return;
      }

      const status = data?.status || "VERIFIED";
      setLicenseStatus(status);
      setLicenseVerifyResult(data);

      if (status === "VERIFIED") {
        toast.success(data?.message || "Medical license verified successfully.");
      } else if (status === "INCONCLUSIVE") {
        toast.error(data?.message || "Medical license verification is inconclusive.");
      } else {
        setLicenseFailedMessage(
          data?.message || "The medical license couldn't be verified. Please verify manually."
        );
        setShowLicenseFailedModal(true);
      }
    } catch (error) {
      console.error("Error verifying medical license:", error);
      toast.error("Something went wrong while verifying the license.");
    } finally {
      setIsVerifyingLicense(false);
    }
  };

  const handleManualLicenseVerify = async () => {
    const email = incomingDoctorData?.email || "";

    if (!email) {
      toast.error("Doctor email not found.");
      return;
    }

    setIsManualVerifyingLicense(true);
    try {
      const token = localStorage.getItem("auth_token");
      const baseUrl = API_URL?.endsWith("/") ? API_URL : `${API_URL}/`;
      const res = await fetch(`${baseUrl}doctor/verification/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          email,
          type: "MCI",
          status: "VERIFIED",
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.error) {
        toast.error(data?.error || data?.message || "Failed to update MCI verification status.");
        return;
      }

      setLicenseStatus("VERIFIED");
      toast.success("Medical license manually verified successfully.");
      setShowLicenseFailedModal(false);
    } catch (error) {
      console.error("Error manually verifying medical license:", error);
      toast.error("Something went wrong while manually verifying the license.");
    } finally {
      setIsManualVerifyingLicense(false);
    }
  };

  const handleVerifyPan = async () => {
    const panNumber = incomingDoctorData?.panNumber || "";
    const panHolderName =
      [
        incomingDoctorData?.salutation,
        incomingDoctorData?.firstName,
        incomingDoctorData?.middleName,
        incomingDoctorData?.lastName,
      ]
        .filter(Boolean)
        .join(" ") || incomingDoctorData?.doctorName || "";
    const email = incomingDoctorData?.email || "";

    if (!panNumber) {
      toast.error("PAN number not found.");
      return;
    }

    if (!email) {
      toast.error("Doctor email not found.");
      return;
    }

    setIsVerifyingPan(true);
    try {
      const token = localStorage.getItem("auth_token");
      const baseUrl = API_URL?.endsWith("/") ? API_URL : `${API_URL}/`;
      const res = await fetch(`${baseUrl}doctor/pan/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          email,
          panNumber,
          panHolderName,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.success === false) {
        toast.error(data?.message || "Failed to verify PAN.");
        return;
      }

      const status = data?.data?.status || "NOT_VERIFIED";
      setPanVerifyStatus(status);

      if (status === "VERIFIED") {
        toast.success(data?.data?.message || "PAN verified successfully.");
      } else if (status === "INCONCLUSIVE") {
        toast.error(data?.data?.message || "PAN verification is inconclusive.");
      } else if (status === "NOT_VERIFIED") {
        setPanFailedMessage(
         
            "The PAN is valid, but the name provided doesn't match our records. Please verify manually."
        );
        setShowPanFailedModal(true);
      } else {
        toast.error(data?.data?.message || "PAN could not be verified.");
      }
    } catch (error) {
      console.error("Error verifying PAN:", error);
      toast.error("Something went wrong while verifying the PAN.");
    } finally {
      setIsVerifyingPan(false);
    }
  };

  const handleManualPanVerify = async () => {
    const email = incomingDoctorData?.email || "";

    if (!email) {
      toast.error("Doctor email not found.");
      return;
    }

    setIsManualVerifyingPan(true);
    try {
      const token = localStorage.getItem("auth_token");
      const baseUrl = API_URL?.endsWith("/") ? API_URL : `${API_URL}/`;
      const res = await fetch(`${baseUrl}doctor/verification/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          email,
          type: "PAN",
          status: true,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.error) {
        toast.error(data?.error || data?.message || "Failed to update PAN verification status.");
        return;
      }

      setPanVerifyStatus("VERIFIED");
      toast.success("PAN manually verified successfully.");
      setShowPanFailedModal(false);
    } catch (error) {
      console.error("Error manually verifying PAN:", error);
      toast.error("Something went wrong while manually verifying the PAN.");
    } finally {
      setIsManualVerifyingPan(false);
    }
  };

  const buildOnboardPayload = (extra = {}) => {
    const fullName =
      [
        incomingDoctorData?.salutation,
        incomingDoctorData?.firstName,
        incomingDoctorData?.middleName,
        incomingDoctorData?.lastName,
      ]
        .filter(Boolean)
        .join(" ") || incomingDoctorData?.doctorName || "";

    const qualifications = incomingDoctorData?.qualification
      ? String(incomingDoctorData.qualification)
          .split(",")
          .map((q) => q.trim())
          .filter(Boolean)
      : [];

    return {
      salutation: incomingDoctorData?.salutation || "",
      fullName,
      phoneNumber: incomingDoctorData?.mobile || incomingDoctorData?.phone || "",
      email: incomingDoctorData?.email || "",
      medicalLicenseNumber:
        incomingDoctorData?.medicalLicenseNumber || incomingDoctorData?.licenseNumber || "",
      specialization: Array.isArray(incomingDoctorData?.specialization)
        ? incomingDoctorData.specialization
        : incomingDoctorData?.specialization
        ? [incomingDoctorData.specialization]
        : [],
      languageSelection: Array.isArray(incomingDoctorData?.languageSelection)
        ? incomingDoctorData.languageSelection
        : incomingDoctorData?.languageSelection
        ? [incomingDoctorData.languageSelection]
        : [],
      qualifications,
      panCardNumber: incomingDoctorData?.panNumber || "",
      panVerified: panVerifyStatus === "VERIFIED",
      mciVerificationStatus: licenseStatus === "VERIFIED" ? "VERIFIED" : "NOT_VERIFIED",
      tenantId: incomingDoctorData?.tenantId || "",
      doctorType,
      doctorSubType,
      ...extra,
    };
  };

  const handleCompleteOnboard = async () => {
    if (!incomingDoctorData?.email) {
      toast.error("Doctor email not found.");
      return;
    }

    if (!doctorType) {
      toast.error("Doctor Category is required.");
      return;
    }

    if (!doctorSubType) {
      toast.error("Doctor Sub Type is required.");
      return;
    }

    setIsCompletingOnboard(true);
    try {
      const token = localStorage.getItem("auth_token");
      const tenantId = incomingDoctorData?.tenantId;

      let panPhotoFile = null;
      if (incomingDoctorData.panImage) {
        const panBlob = await (await fetch(incomingDoctorData.panImage)).blob();
        panPhotoFile = new File([panBlob], "panPhoto.png", {
          type: panBlob.type || "image/png",
        });
      }

      let agreementPdfFile = null;
      if (incomingDoctorData.agreementPdfUrl) {
        const agreementBlob = await (await fetch(incomingDoctorData.agreementPdfUrl)).blob();
        agreementPdfFile = new File([agreementBlob], "agreementPdf.pdf", {
          type: agreementBlob.type || "application/pdf",
        });
      }

      // Step 1: complete-onboard without tenant
      const step1FormData = new FormData();
      step1FormData.append("doctor", JSON.stringify(buildOnboardPayload()));
      if (panPhotoFile) step1FormData.append("panPhoto", panPhotoFile);
      if (agreementPdfFile) step1FormData.append("agreementPdf", agreementPdfFile);

      const step1Res = await fetch(`${API_URL}doctor/complete-onboard`, {
        method: "POST",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: step1FormData,
      });

      const step1Result = await step1Res.json().catch(() => ({}));

      if (!step1Res.ok || step1Result?.status !== "SUCCESS") {
        toast.error(step1Result?.message || "Failed to complete doctor onboarding.");
        return;
      }

      // Step 2: complete-onboard with tenant, including doctorCode / doctorProfileId from step 1
      const step2FormData = new FormData();
      step2FormData.append(
        "doctor",
        JSON.stringify(
          buildOnboardPayload({
            doctorCode: step1Result.doctorCode,
            id: step1Result.doctorProfileId,
          })
        )
      );
      if (panPhotoFile) step2FormData.append("panPhoto", panPhotoFile);
      if (agreementPdfFile) step2FormData.append("agreementPdf", agreementPdfFile);

      const step2Res = await fetch(`${API_URL}doctor/complete-onboard`, {
        method: "POST",
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(tenantId && { tenantId }),
        },
        body: step2FormData,
      });

      const step2Result = await step2Res.json().catch(() => ({}));

      if (!step2Res.ok) {
        toast.error(step2Result?.message || "Failed to complete doctor onboarding.");
        return;
      }

      toast.success("Doctor onboarding completed successfully.");
      navigate(APP_ROUTES.CUREBAY_DOCTOR_LIST);
    } catch (error) {
      console.error("Error completing doctor onboarding:", error);
      toast.error("Something went wrong while completing onboarding.");
    } finally {
      setIsCompletingOnboard(false);
    }
  };
  const handleReject = async () => {
    if (!incomingDoctorData?.email) {
      toast.error("Doctor email not found.");
      return;
    }

    if (!rejectRemark.trim()) {
      toast.error("Please enter a reason for rejection.");
      return;
    }

    setIsApproving(true);
    try {
      const token = localStorage.getItem("auth_token");

      const res = await fetch(`${API_URL}auth/onboard/registration-approval`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          email: incomingDoctorData.email,
          rejected: true,
          rejectRemark: rejectRemark.trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.success === false) {
        toast.error(data?.message || "Failed to reject doctor.");
        return;
      }

      setFinanceApprovalStatus(2);
      toast.success("Doctor rejected successfully.");
      setShowRejectModal(false);
      setRejectRemark("");
      navigate(APP_ROUTES.CUREBAY_DOCTOR_LIST);
    } catch (error) {
      console.error("Error rejecting doctor:", error);
      toast.error("Something went wrong while rejecting the doctor.");
    } finally {
      setIsApproving(false);
    }
  };
  // const handleApprove = async () => {
  //   console.log(incomingDoctorData,"dcfgvhbjnk")
  //   if (!incomingDoctorData?.email) {
  //     toast.error("Doctor email not found.");
  //     return;
  //   }

  //   setIsApproving(true);
  //   try {
  //     const token = localStorage.getItem("auth_token");
  //     const tenantId = incomingDoctorData?.tenantId;

  //     const formData = new FormData();
  //     formData.append(
  //       "request",
  //       new Blob([JSON.stringify({ approved: true })], { type: "application/json" })
  //     );

  //     if (incomingDoctorData.panImage) {
  //       const panBlob = await (await fetch(incomingDoctorData.panImage)).blob();
  //       formData.append("panPhoto", panBlob, "panPhoto.png");
  //     }

  //     const res = await fetch(
  //       `${API_URL}auth/onboard/finance-approval?email=${incomingDoctorData.email}`,
  //       {
  //         method: "PUT",
  //         headers: {
  //           ...(token && { Authorization: `Bearer ${token}` }),
  //           ...(tenantId && { "X-Tenant-Id": tenantId }),
  //         },
  //         body: formData,
  //       }
  //     );

  //     const data = await res.json().catch(() => ({}));

  //     if (!res.ok || data?.success === false) {
  //       toast.error(data?.message || "Failed to approve doctor.");
  //       return;
  //     }

  //     setFinanceApprovalStatus(1);
  //     toast.success("Doctor approved successfully.");
  //     navigate(APP_ROUTES.CUREBAY_DOCTOR_LIST);
  //   } catch (error) {
  //     console.error("Error approving doctor:", error);
  //     toast.error("Something went wrong while approving the doctor.");
  //   } finally {
  //     setIsApproving(false);
  //   }
  // };

 

  const isLoading = isSendingOtp || isVerifyingOtp;

  return (
    <>
      {isLoading && <FullScreenLoader message="" />}

      <DashboardLayout>
        <div className="min-h-screen bg-white p-4 md:pt-10">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-50 p-2.5 rounded-xl border border-emerald-100 flex items-center justify-center">
              <ShieldCheck size={26} className="text-emerald-600" />
            </div>
            <h2 className="text-lg font-bold text-[#334155] md:text-xl tracking-tight">
              Doctor Verification
            </h2>
          </div>

          <div className="mt-8 space-y-8">
            {incomingDoctorData && (
              <div className="rounded-lg border border-gray-200 p-6 shadow-sm">
                <div className="mb-4 flex justify-end">
                  <span className="text-xs text-gray-500">
                    Submitted On:{" "}
                    <span className="font-medium text-slate-700">
                      {formatDateTime(incomingDoctorData.createdAt)}
                    </span>
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <DetailField
                    label="Doctor Name"
                    value={[
                      incomingDoctorData.salutation,
                      incomingDoctorData.firstName,
                      incomingDoctorData.middleName,
                      incomingDoctorData.lastName,
                    ]
                      .filter(Boolean)
                      .join(" ") || incomingDoctorData.doctorName}
                  />
                  <div className="flex flex-col">
                    <label className="text-sm text-[#004276] mb-1 font-medium">
                      Medical License Number
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-slate-700 shadow-sm min-h-[38px] flex items-center break-words">
                        {incomingDoctorData.medicalLicenseNumber || incomingDoctorData.licenseNumber || "-"}
                      </div>
                      {(incomingDoctorData.medicalLicenseNumber || incomingDoctorData.licenseNumber) && (
                        <button
                          type="button"
                          onClick={handleVerifyLicense}
                          disabled={isVerifyingLicense || licenseStatus === "VERIFIED"}
                          className="shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#004276] hover:bg-[#003a5c] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {licenseStatus === "VERIFIED"
                            ? "Verified"
                            : isVerifyingLicense
                            ? "Verifying..."
                            : "Verify"}
                        </button>
                      )}
                      {licenseVerifyResult && (
                        <button
                          type="button"
                          onClick={() => setShowLicenseModal(true)}
                          className="shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium text-[#004276] border border-[#004276] hover:bg-[#004276]/5"
                        >
                          View
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm text-[#004276] mb-1 font-medium">
                      Doctor Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="doctorType"
                      value={doctorType}
                      onChange={handleDoctorTypeChange}
                      className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
                    >
                      <option value="">Select doctor category</option>
                      <option value="EMPANELLED">Empanel</option>
                      <option value="INVENTORY">Inventory</option>
                      <option value="FREELANCER">Freelancer</option>
                    </select>
                  </div>
                  {(doctorType === "EMPANELLED" || doctorType === "INVENTORY" || doctorType === "FREELANCER") && (
                    <div className="flex flex-col">
                      <label className="text-sm text-[#004276] mb-1 font-medium">
                        Doctor Sub Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="doctorSubType"
                        value={doctorSubType}
                        onChange={(e) => setDoctorSubType(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition"
                      >
                        <option value="">Select doctor sub type</option>
                        <option value="GP">GP</option>
                        <option value="SP">SP</option>
                      </select>
                    </div>
                  )}
                  <DetailField label="Qualification" value={incomingDoctorData.qualification} />
                  <DetailField
                    label="Specialization"
                    value={
                      Array.isArray(incomingDoctorData.specialization)
                        ? incomingDoctorData.specialization.join(", ")
                        : incomingDoctorData.specialization
                    }
                  />
                  <DetailField label="Year of Passing" value={incomingDoctorData.yearOfPassing} />
                  <DetailField label="State Medical Council" value={incomingDoctorData.stateMedicalCouncil} />
                  <DetailField label="Phone Number" value={incomingDoctorData.mobile || incomingDoctorData.phone} />
                  <DetailField label="Email Id" value={incomingDoctorData.email} />
                  <div className="flex flex-col">
                    <label className="text-sm text-[#004276] mb-1 font-medium">
                      PAN Number
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-slate-700 shadow-sm min-h-[38px] flex items-center break-words">
                        {incomingDoctorData.panNumber || "-"}
                      </div>
                      <button
                        type="button"
                        onClick={handleVerifyPan}
                        disabled={isVerifyingPan || panVerifyStatus === "VERIFIED"}
                        className="shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#004276] hover:bg-[#003a5c] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {panVerifyStatus === "VERIFIED"
                          ? "Verified"
                          : isVerifyingPan
                          ? "Verifying..."
                          : "Verify"}
                      </button>
                    </div>
                  </div>
                  <DetailField
                    label="Languages Known"
                    value={
                      Array.isArray(incomingDoctorData.languageSelection)
                        ? incomingDoctorData.languageSelection.join(", ")
                        : incomingDoctorData.languageSelection
                    }
                  />
                  <DetailField label="Referral Code" value={incomingDoctorData.referralCode} />
                 

                  {incomingDoctorData.panImage && (
                    <div className="flex flex-col">
                      <label className="text-sm text-[#004276] mb-1 font-medium">
                        PAN Photo
                      </label>
                      <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 shadow-sm min-h-[38px] flex items-center">
                        <button
                          type="button"
                          onClick={() => setShowPanModal(true)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#004276] hover:text-[#003a5c] hover:underline"
                        >
                          <FileText size={16} />
                          View Document
                        </button>
                      </div>
                    </div>
                  )}
                  {incomingDoctorData.agreementPdfUrl && (
                    <div className="flex flex-col">
                      <label className="text-sm text-[#004276] mb-1 font-medium">
                        Terms of Engagement Document
                      </label>
                      <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 shadow-sm min-h-[38px] flex items-center">
                        <button
                          type="button"
                          onClick={() => setShowAgreementModal(true)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-[#004276] hover:text-[#003a5c] hover:underline"
                        >
                          <FileText size={16} />
                          View Document
                        </button>
                      </div>
                    </div>
                  )}
                </div>

               

                <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
                  {panVerifyStatus !== "INCONCLUSIVE" && (
                  <button
                    type="button"
                    onClick={handleCompleteOnboard}
                    disabled={isCompletingOnboard || panVerifyStatus !== "VERIFIED" || !doctorSubType}
                               className="rounded-full bg-gradient-to-r from-[#004172] to-[#3cba84] px-8 py-2.5 text-sm font-medium text-white transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isCompletingOnboard ? "Submitting..." : "Approve"}
                  </button>
                  )}
                  {panVerifyStatus !== "INCONCLUSIVE" && (
                  <button
                    type="button"
                    onClick={() => setShowRejectModal(true)}
                    disabled={
                      isApproving ||
                      financeApprovalStatus === 2 ||
                      panVerifyStatus !== "VERIFIED"
                    }
                    className="rounded-full bg-gradient-to-r from-[#c0392b] to-[#e74c3c] px-8 py-2.5 text-sm font-medium text-white transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {financeApprovalStatus === 2
                      ? "Rejected"
                      : isApproving
                      ? "Loading..."
                      : "Reject"}
                  </button>
                  )}
                </div>
              </div>
            )}

           
          </div>
        </div>
      </DashboardLayout>

      {showPanModal && incomingDoctorData?.panImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowPanModal(false)}
        >
          <div
            className="relative w-full max-w-2xl rounded-lg bg-white p-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#004276]">PAN Document</h3>
              <button
                type="button"
                onClick={() => setShowPanModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <img
              src={incomingDoctorData.panImage}
              alt="PAN"
              className="max-h-[70vh] w-full rounded-md object-contain"
            />
          </div>
        </div>
      )}

      {showAgreementModal && incomingDoctorData?.agreementPdfUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowAgreementModal(false)}
        >
          <div
            className="relative w-[70vw] h-[80vh] rounded-lg bg-white p-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#004276]">Terms of Engagement Document</h3>
              <button
                type="button"
                onClick={() => setShowAgreementModal(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <iframe
              src={incomingDoctorData.agreementPdfUrl}
              className="h-[calc(100%-2.5rem)] w-full rounded-md border-0"
              title="Terms of Engagement Document"
            />
          </div>
        </div>
      )}

      {showPanFailedModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !isManualVerifyingPan && setShowPanFailedModal(false)}
        >
          <div
            className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-red-600">PAN Verification Failed</h3>
            <p className="mt-3 text-sm text-slate-600">{panFailedMessage}</p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowPanFailedModal(false)}
                disabled={isManualVerifyingPan}
                className="px-6 py-2 rounded-lg border border-red-300 text-red-600 font-medium hover:bg-red-50 disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleManualPanVerify}
                disabled={isManualVerifyingPan}
                className="px-6 py-2 rounded-lg bg-[#004276] text-white font-medium hover:bg-[#003a5c] disabled:opacity-50"
              >
                {isManualVerifyingPan ? "Verifying..." : " Verify Manually"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLicenseFailedModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !isManualVerifyingLicense && setShowLicenseFailedModal(false)}
        >
          <div
            className="relative w-full max-w-2xl rounded-xl bg-white shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 bg-red-50 px-6 py-4">
              <div>
                <h3 className="text-lg font-bold text-red-600">MCI Verification Failed</h3>
                <p className="mt-1 text-sm text-slate-600">{licenseFailedMessage}</p>
              </div>
              <button
                type="button"
                onClick={() => !isManualVerifyingLicense && setShowLicenseFailedModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {licenseVerifyResult?.result && (
              <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <ModalDetailField
                    label="Doctor Name"
                    value={licenseVerifyResult?.result?.doctorName}
                  />
                  <ModalDetailField
                    label="Parent's Name"
                    value={licenseVerifyResult?.result?.parentName}
                  />
                  <ModalDetailField
                    label="Registration Number"
                    value={licenseVerifyResult?.result?.registrationNo}
                  />
                  <ModalDetailField
                    label="Registration Date"
                    value={licenseVerifyResult?.result?.registrationDate}
                  />
                  <ModalDetailField
                    label="State Medical Council"
                    value={licenseVerifyResult?.result?.stateMedicalCouncil}
                  />
                  <ModalDetailField
                    label="Qualification"
                    value={licenseVerifyResult?.result?.qualification}
                  />
                  <ModalDetailField
                    label="Qualification Year"
                    value={licenseVerifyResult?.result?.qualificationYear}
                  />
                  <ModalDetailField
                    label="Date of Birth"
                    value={licenseVerifyResult?.result?.dateOfBirth}
                  />
                  <ModalDetailField
                    label="Address"
                    value={licenseVerifyResult?.result?.address}
                    fullWidth
                  />
                  <ModalDetailField
                    label="Source"
                    value={licenseVerifyResult?.result?.source}
                  />
                  <ModalDetailField
                    label="Checked At"
                    value={formatDateTime(licenseVerifyResult?.checkedAt)}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-3">
              <button
                type="button"
                onClick={() => setShowLicenseFailedModal(false)}
                disabled={isManualVerifyingLicense}
                className="px-6 py-2 rounded-lg border border-red-300 text-red-600 font-medium hover:bg-red-50 disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleManualLicenseVerify}
                disabled={isManualVerifyingLicense}
                className="px-6 py-2 rounded-lg bg-[#004276] text-white font-medium hover:bg-[#003a5c] disabled:opacity-50"
              >
                {isManualVerifyingLicense ? "Verifying..." : "Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLicenseModal && licenseVerifyResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShowLicenseModal(false)}
        >
          <div
            className="relative w-full max-w-2xl rounded-xl bg-white shadow-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 bg-emerald-50 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100">
                  <BadgeCheck size={20} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    NMC Verification Successful
                  </h3>
                  <p className="text-xs text-slate-500">
                    {licenseVerifyResult?.message ||
                      "Registration number is valid and active on the NMC Indian Medical Register."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowLicenseModal(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <ModalDetailField
                  label="Doctor Name"
                  value={licenseVerifyResult?.result?.doctorName}
                />
                <ModalDetailField
                  label="Parent's Name"
                  value={licenseVerifyResult?.result?.parentName}
                />
                <ModalDetailField
                  label="Registration Number"
                  value={licenseVerifyResult?.result?.registrationNo}
                />
                <ModalDetailField
                  label="Registration Date"
                  value={licenseVerifyResult?.result?.registrationDate}
                />
                <ModalDetailField
                  label="State Medical Council"
                  value={licenseVerifyResult?.result?.stateMedicalCouncil}
                />
                <ModalDetailField
                  label="Qualification"
                  value={licenseVerifyResult?.result?.qualification}
                />
                <ModalDetailField
                  label="Qualification Year"
                  value={licenseVerifyResult?.result?.qualificationYear}
                />
                <ModalDetailField
                  label="Date of Birth"
                  value={licenseVerifyResult?.result?.dateOfBirth}
                />
                <ModalDetailField
                  label="Address"
                  value={licenseVerifyResult?.result?.address}
                  fullWidth
                />
                <ModalDetailField
                  label="Source"
                  value={licenseVerifyResult?.result?.source}
                />
                <ModalDetailField
                  label="Checked At"
                  value={formatDateTime(licenseVerifyResult?.checkedAt)}
                />
                
              </div>
            </div>

            <div className="flex justify-end border-t border-gray-100 px-6 py-3">
              <button
                type="button"
                onClick={() => setShowLicenseModal(false)}
                className="rounded-full bg-gradient-to-r from-[#004172] to-[#3cba84] px-6 py-2 text-sm font-medium text-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => !isApproving && setShowRejectModal(false)}
        >
          <div
            className="relative w-full max-w-md rounded-lg bg-white p-5 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#004276]">Reject Doctor</h3>
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                disabled={isApproving}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <label className="text-sm text-[#004276] mb-1 font-medium block">
              Reason for Rejection <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectRemark}
              onChange={(e) => setRejectRemark(e.target.value)}
              rows={4}
              placeholder="Enter reason for rejection"
              className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#e74c3c] transition resize-none"
            />
            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowRejectModal(false)}
                disabled={isApproving}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={isApproving}
                className="px-6 py-2 rounded-lg bg-gradient-to-r from-[#c0392b] to-[#e74c3c] text-white font-medium disabled:opacity-50"
              >
                {isApproving ? "Rejecting..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DetailField({ label, value }) {
  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">{label}</label>
      <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-slate-700 shadow-sm min-h-[38px] flex items-center break-words">
        {value != null && value !== "" ? value : "-"}
      </div>
    </div>
  );
}

function ModalDetailField({ label, value, fullWidth }) {
  return (
    <div className={`flex flex-col ${fullWidth ? "md:col-span-2" : ""}`}>
      <label className="text-xs font-medium text-slate-500 mb-1">{label}</label>
      <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-slate-700 break-words">
        {value != null && value !== "" ? value : "-"}
      </div>
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  showRequired = true,
  error,
  ...rest
}) {
  return (
    <div className="flex flex-col">
      <label className="text-sm text-[#004276] mb-1 font-medium">
        {label} {showRequired && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder="Enter here"
        required={showRequired}
        className={`w-full rounded-lg border px-4 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#3cba84] transition ${
          error ? "border-red-500" : "border-gray-200"
        }`}
        {...rest}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export default CurebayDoctorVerify;
